import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  IconButton,
  Divider,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  NavigationOutlined,
  TouchAppOutlined,
  SearchOutlined,
  CheckBoxOutlined,
  DialpadOutlined,
  MoreHorizOutlined,
} from '@mui/icons-material';
import { useShortcutsContext } from '../context/ShortcutsContext';
import { SHORTCUT_CATEGORIES, normalizeShortcut } from '../data/shortcuts';

const CATEGORY_ICONS = {
  [SHORTCUT_CATEGORIES.NAVIGATION]: <NavigationOutlined fontSize="small" />,
  [SHORTCUT_CATEGORIES.ACTIONS]: <TouchAppOutlined fontSize="small" />,
  [SHORTCUT_CATEGORIES.SEARCH]: <SearchOutlined fontSize="small" />,
  [SHORTCUT_CATEGORIES.SELECTION]: <CheckBoxOutlined fontSize="small" />,
  [SHORTCUT_CATEGORIES.MODAL]: <DialpadOutlined fontSize="small" />,
  [SHORTCUT_CATEGORIES.OTHER]: <MoreHorizOutlined fontSize="small" />,
};

const CATEGORY_LABELS = {
  [SHORTCUT_CATEGORIES.NAVIGATION]: 'Navigation',
  [SHORTCUT_CATEGORIES.ACTIONS]: 'Actions',
  [SHORTCUT_CATEGORIES.SEARCH]: 'Search',
  [SHORTCUT_CATEGORIES.SELECTION]: 'Selection',
  [SHORTCUT_CATEGORIES.MODAL]: 'Modal/Form',
  [SHORTCUT_CATEGORIES.OTHER]: 'Other',
};

const ShortcutsDialog = ({ open, onClose }) => {
  const { shortcuts, vimMode, resetShortcuts } = useShortcutsContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Group shortcuts by category
  const groupedShortcuts = useMemo(() => {
    const groups = {};

    Object.entries(shortcuts).forEach(([key, shortcut]) => {
      if (!shortcut.enabled && shortcut.vim && !vimMode) return;

      const category = shortcut.category || SHORTCUT_CATEGORIES.OTHER;

      if (!groups[category]) {
        groups[category] = [];
      }

      groups[category].push({
        key,
        ...shortcut,
      });
    });

    // Sort within each category
    Object.keys(groups).forEach(category => {
      groups[category].sort((a, b) => a.description.localeCompare(b.description));
    });

    return groups;
  }, [shortcuts, vimMode]);

  // Filter shortcuts based on search and category
  const filteredShortcuts = useMemo(() => {
    const query = searchQuery.toLowerCase();
    const filtered = {};

    Object.entries(groupedShortcuts).forEach(([category, shortcuts]) => {
      if (selectedCategory !== 'all' && category !== selectedCategory) return;

      const matchingShortcuts = shortcuts.filter(shortcut =>
        shortcut.description.toLowerCase().includes(query) ||
        shortcut.key.toLowerCase().includes(query) ||
        shortcut.action.toLowerCase().includes(query)
      );

      if (matchingShortcuts.length > 0) {
        filtered[category] = matchingShortcuts;
      }
    });

    return filtered;
  }, [groupedShortcuts, searchQuery, selectedCategory]);

  const handlePrint = () => {
    window.print();
  };

  const handleReset = () => {
    if (window.confirm('Reset all shortcuts to defaults?')) {
      resetShortcuts();
    }
  };

  const totalShortcuts = Object.values(groupedShortcuts).reduce(
    (sum, shortcuts) => sum + shortcuts.length,
    0
  );

  const filteredCount = Object.values(filteredShortcuts).reduce(
    (sum, shortcuts) => sum + shortcuts.length,
    0
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          height: '80vh',
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">Keyboard Shortcuts</Typography>
            <Chip
              label={`${totalShortcuts} shortcuts`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton size="small" onClick={handlePrint} title="Print shortcuts">
              <PrintIcon />
            </IconButton>
            <IconButton size="small" onClick={handleReset} title="Reset to defaults">
              <RefreshIcon />
            </IconButton>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Search */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search shortcuts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Category Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={selectedCategory}
            onChange={(e, value) => setSelectedCategory(value)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="All" value="all" />
            {Object.entries(CATEGORY_LABELS).map(([category, label]) => (
              groupedShortcuts[category] && (
                <Tab
                  key={category}
                  label={label}
                  value={category}
                  icon={CATEGORY_ICONS[category]}
                  iconPosition="start"
                />
              )
            ))}
          </Tabs>
        </Box>

        {/* Vim Mode Notice */}
        {vimMode && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Vim-style navigation (h/j/k/l) is enabled. You can disable it in Settings.
          </Alert>
        )}

        {/* Shortcuts Table */}
        {Object.keys(filteredShortcuts).length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              {searchQuery ? `No shortcuts found for "${searchQuery}"` : 'No shortcuts available'}
            </Typography>
          </Box>
        ) : (
          Object.entries(filteredShortcuts).map(([category, shortcuts]) => (
            <Box key={category} sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                {CATEGORY_ICONS[category]}
                <Typography variant="h6" color="primary">
                  {CATEGORY_LABELS[category]}
                </Typography>
                <Chip label={shortcuts.length} size="small" />
              </Box>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width="40%">
                        <Typography variant="caption" fontWeight={600}>
                          Description
                        </Typography>
                      </TableCell>
                      <TableCell width="30%">
                        <Typography variant="caption" fontWeight={600}>
                          Shortcut
                        </Typography>
                      </TableCell>
                      <TableCell width="30%">
                        <Typography variant="caption" fontWeight={600}>
                          Scope
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shortcuts.map((shortcut) => (
                      <TableRow key={shortcut.key} hover>
                        <TableCell>
                          <Typography variant="body2">{shortcut.description}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={normalizeShortcut(shortcut.key)}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontFamily: 'monospace',
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={shortcut.scope}
                            size="small"
                            color={
                              shortcut.scope === 'global'
                                ? 'primary'
                                : shortcut.scope === 'page'
                                ? 'secondary'
                                : 'default'
                            }
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ))
        )}

        {/* Tips */}
        <Box sx={{ mt: 4, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Tips:
          </Typography>
          <Typography variant="body2" color="text.secondary" component="div">
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>Press <strong>?</strong> anytime to open this dialog</li>
              <li>Use <strong>g + letter</strong> for quick navigation (e.g., g d for Dashboard)</li>
              <li>Press <strong>{navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? '⌘' : 'Ctrl'}+Shift+P</strong> to open the command palette</li>
              <li>Most shortcuts are disabled when typing in input fields</li>
              {vimMode && <li>Vim navigation (j/k) works in tables and lists</li>}
            </ul>
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, ml: 1 }}>
          {filteredCount !== totalShortcuts && `Showing ${filteredCount} of ${totalShortcuts} shortcuts`}
        </Typography>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShortcutsDialog;
